"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { HamburgerMenuIcon } from "../common/icons";
import punchupIcon from "../../resources/punchupicon.svg";
import { Comedian } from "@punchup/data";
import { ComedianSelectorDrawer } from "./ComedianSelectorDrawer";
import { useDashboardMenuItems } from "./useDashboardMenuItems";
import { useComedianSettingsNav } from "./useComedianSettingsNav";
import { SlideOutDrawer } from "./SlideOutDrawer";
import { dashboardFooterItems } from "./dashboardFooterItems";
import Image from "next/image";

interface DashboardMobileHeaderProps {
  comedians: Comedian[];
  selectedComedian: Comedian | null;
  isLoading: boolean;
  comedianSlug: string;
  isSuperAdmin?: boolean;
  /** The settings gate's answer, read by the host. */
  settingsNav?: boolean;
  /** The Automations gate's answer; adds Plan & usage and Billing to Settings. */
  settingsPlanBilling?: boolean;
}

export function DashboardMobileHeader({
  comedians,
  selectedComedian,
  isLoading,
  comedianSlug,
  isSuperAdmin,
  settingsNav = false,
  settingsPlanBilling = false,
}: DashboardMobileHeaderProps) {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Close drawer on navigation
  useEffect(() => {
    setIsOpen(false);
    setSettingsOpen(false);
  }, [pathname]);

  const menuItems = useDashboardMenuItems({
    comedianSlug,
    selectedComedian,
    showAllSubitems: true,
    settingsNav,
  });
  const settings = useComedianSettingsNav({
    comedianSlug,
    selectedComedian,
    enabled: settingsNav,
    planBilling: settingsPlanBilling,
  });

  return (
    <>
      <header className="fixed top-app-banner z-[51] h-header-bar w-full bg-ivory border-b border-[#e7dac7] lg:hidden">
        <nav
          className="h-header-bar flex flex-row items-center w-full px-4 py-1.5"
          aria-label="Dashboard navigation"
        >
          <button
            type="button"
            aria-label="Open navigation menu"
            className="relative flex flex-col justify-center p-2 -ml-2 rounded-md transition-colors ease-in-out duration-200 shrink-0"
            onClick={() => {
              setSettingsOpen(Boolean(settings?.drilldown));
              setIsOpen(true);
            }}
          >
            <HamburgerMenuIcon className="w-[28px] h-[28px] stroke-primary-blue" />
          </button>

          <div className="flex-1 min-w-0">
            <ComedianSelectorDrawer
              comedians={comedians}
              selectedComedian={selectedComedian}
              isLoading={isLoading}
              isSuperAdmin={isSuperAdmin}
            />
          </div>

          <Image
            src={punchupIcon}
            aria-label="Punchup icon"
            alt="Punchup icon"
            width={30}
            height={15}
            className="lg:hidden" // hide on desktop
          />
        </nav>
      </header>

      <SlideOutDrawer
        open={isOpen}
        onOpenChange={setIsOpen}
        menuItems={menuItems}
        footerItems={dashboardFooterItems({
          isSuperAdmin,
          settingsEntry: settings && {
            ...settings.entry,
            onSelect: () => setSettingsOpen(true),
          },
        })}
        drilldown={
          settings && settingsOpen
            ? {
                label: settings.entry.label,
                title: settings.entry.title,
                onBack: () => setSettingsOpen(false),
                items: settings.items,
              }
            : undefined
        }
        onItemClick={settings?.onItemClick}
      />
    </>
  );
}
